import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileBarChart, Loader2 } from "lucide-react";
import InvestorShell from "./InvestorShell";
import { api, money, Badge } from "./api";

export default function Statements() {
  const [stmts, setStmts] = useState(null);
  const [dists, setDists] = useState([]);
  const [gates, setGates] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    api.get("/investor/statements").then(({ data }) => setStmts(data)).catch((e) => {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error("Could not load statements");
      setStmts([]);
    });
    api.get("/investor/distributions").then(({ data }) => setDists(data)).catch(() => {});
    api.get("/investor/me").then(({ data }) => setGates(data.gates)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (s) => {
    try {
      const { data } = await api.get(`/investor/statements/${s.statement_id}/download`, { responseType: "text" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `statement-${s.statement_id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <InvestorShell gates={gates} title="Vehicle Statements & Distributions" subtitle="Approved investors · vehicle-specific reporting">
      {forbidden ? (
        <div className="card-line mx-auto max-w-lg rounded-2xl bg-white p-10 text-center" data-testid="statements-locked">
          <FileBarChart size={30} className="mx-auto text-red-700" />
          <h2 className="mt-4 font-display text-lg font-extrabold text-offwhite">Approved investors only</h2>
          <p className="mt-2 text-sm text-muted">Statements become available after Xtreme Exotics approves your investor status and assigns vehicles.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="statements-page">
          {!stmts && <p className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-red-700" /></p>}
          {stmts && !stmts.length && <p className="card-line rounded-xl bg-white p-10 text-center text-sm text-muted">No published statements yet for your assigned vehicles.</p>}
          {stmts?.map((s) => (
            <div key={s.statement_id} className="card-line rounded-xl bg-white p-6" data-testid={`statement-${s.statement_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-base font-extrabold text-offwhite">{s.vehicle_name} — {s.period_label}</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-muted">Published {new Date(s.published_at).toLocaleDateString()} · Informational</p>
                </div>
                <button onClick={() => download(s)} className="btn-ghost !px-4 !py-2 !text-xs" data-testid={`download-${s.statement_id}`}><Download size={13} /> Download CSV</button>
              </div>
              <div className="mt-4 grid gap-x-10 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                {[
                  ["Reporting period revenue", money(s.gross_revenue)],
                  ["Eligible completed rental hours", s.eligible_hours],
                  ["Net rental revenue", money(s.net_rental_revenue)],
                  [`Fleet partner payout (${s.partner_pct_applied}%)`, money(s.fleet_partner_payout)],
                  ["Itemized expenses", money(s.expenses_total)],
                  ["Reserves", money((s.maintenance_reserve || 0) + (s.other_reserves || 0))],
                  ["Defined Vehicle Profit", money(s.defined_vehicle_profit)],
                  [`Your participation (${s.my_participation.profit_pct}% of DVP)`, money(s.my_participation.distribution)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-3 border-b border-black/[0.05] pb-1.5">
                    <span className="text-muted">{k}</span><span className="font-mono font-bold text-offwhite">{v}</span>
                  </div>
                ))}
              </div>
              {s.expenses?.length > 0 && (
                <div className="mt-3 rounded-lg bg-surface/60 p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted">Expense detail</p>
                  {s.expenses.map((e, i) => (
                    <p key={i} className="mt-1 flex justify-between text-xs text-muted"><span>{e.label}</span><span>{money(e.amount)}</span></p>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="card-line rounded-xl bg-white p-6" data-testid="distributions-panel">
            <h2 className="font-display text-base font-extrabold text-offwhite">Distributions</h2>
            {!dists.length && <p className="mt-3 text-sm text-muted">No distributions recorded yet.</p>}
            <div className="mt-4 space-y-2">
              {dists.map((d) => (
                <div key={d.distribution_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-black/[0.08] p-3" data-testid={`distribution-${d.distribution_id}`}>
                  <div>
                    <p className="text-sm font-bold text-offwhite">{d.vehicle_name} — {d.period_label}</p>
                    <p className="text-[11px] text-muted">{new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-bold text-offwhite">{money(d.amount)}</span>
                    <Badge value={d.status} testid={`distribution-status-${d.distribution_id}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </InvestorShell>
  );
}
